/**
 * Wire types for the Connections tab: agent access, the URL, and what is connected.
 *
 * The whole tab answers one sentence somebody has to be able to act on — *"what is
 * currently allowed to act as me, and how do I stop it?"* — so these types carry exactly
 * what answers that and nothing else.
 *
 * ── One flag, because there is one account ───────────────────────────────────
 *
 * Lunicle's equivalent carries an `isAllowed` beside [McpState.isEnabled], because it is
 * multi-tenant: an administrator permits an account to have agent access and the person
 * then enables it. **LunaPin serves exactly one account**, named by `LUNAPIN_USERNAME` —
 * see `Users.kt` — so there is no second axis and no field for one here. There is nobody
 * it could report on, and a boolean that is always true is a control somebody will
 * eventually try to make mean something.
 *
 * @see LunaPinApi.mcpState
 * @see ApiRoutes.MCP
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * One agent that can act as this account.
 *
 * @property clientId what Revoke names. **Not a secret** — it identifies a registration,
 *   and every route that takes it works out who is asking from the session, so knowing one
 *   grants nothing.
 * @property clientName what the agent called itself when it registered.
 *
 *   **Self-reported, and therefore chosen by a stranger.** RFC 7591 registration is
 *   unauthenticated by design — that is what makes `claude mcp add` need no secret — so
 *   anyone can register a client calling itself "Claude Code". A view must render this as
 *   text and never as markup, and must not let it be the only thing shown before a revoke.
 *   It is here because a list of opaque ids would be unusable, not because it is
 *   trustworthy.
 * @property connectedAt when this agent was first authorized, epoch millis. The **oldest**
 *   surviving row of the grant rather than the newest, so an hourly refresh does not keep
 *   resetting it — see the server's `OAuthTokens.sq`.
 * @property lastUsedAt when it last called, epoch millis, or null if it never has. The
 *   fact people actually act on: *connected in July, never used since* is what makes
 *   somebody press Revoke.
 */
@Serializable
data class McpConnection(
    val clientId: String,
    val clientName: String,
    val connectedAt: Long,
    val lastUsedAt: Long? = null,
)

/**
 * The Connections tab, whole.
 *
 * Returned in full by all three MCP routes rather than each answering a fragment, matching
 * how [ApiRoutes.SESSION] answers a whole [SessionState]. The client re-renders from one
 * object and never has to merge two — so a revoke that also changed the switch, or a switch
 * that also emptied the list, cannot produce a view that disagrees with the server about
 * either half.
 *
 * @property isEnabled whether this account has turned agent access on.
 *
 *   Unlike most booleans on this wire it is **not an affordance**: it mirrors a real
 *   server-side gate. `/oauth/authorize` refuses to start a flow while it is off, the token
 *   endpoint refuses to issue, and `/mcp` rejects every token — all re-read per request, so
 *   flipping this takes effect on the next call in both directions. Setting it to `true` in
 *   a browser console changes a picture and nothing else.
 * @property serverUrl the absolute `/mcp` URL to paste into an agent, **computed by the
 *   server from the origin the request actually arrived on**. The client must not assemble
 *   it from `location`: there may be a reverse proxy in front of a deployment, and the
 *   symptom of getting this wrong is a copied command that fails only on the machine of the
 *   person who pasted it. See the server's `serverOrigin`, which is also what the discovery
 *   documents and every token's `resource` are built from — one function underneath all
 *   three, because an MCP client compares them byte for byte.
 *
 *   Empty only for a state nothing fetched, which is why [McpState] has defaults at all.
 * @property connections what can act as this account right now, oldest first. Empty is the
 *   common case and is a state worth rendering as a sentence rather than as a blank panel.
 */
@Serializable
data class McpState(
    val isEnabled: Boolean = false,
    val serverUrl: String = "",
    val connections: List<McpConnection> = emptyList(),
)

/**
 * "Turn agent access on, or off."
 *
 * Names the **desired state** rather than saying "toggle", deliberately. A toggle is a
 * request whose meaning depends on what the server currently thinks, so two of them racing
 * — a double click, a retried request — can land on either answer. This one says the same
 * thing twice when it is sent twice.
 *
 * @property isEnabled the state to move to.
 */
@Serializable
data class McpEnabledRequest(
    val isEnabled: Boolean,
)
